import { type CSSProperties, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { StageStepperItem } from './StageStepperItem';
import {
  DEFAULT_STAGE_STEPPER_STYLES,
  type StageStepperDisplayMode,
  type StageStepperItemState,
  type StageStepperStyles,
} from './stageStepperStyles';
import { StageStepperSummary } from './StageStepperSummary';

export interface StageStepperStage {
  id: string;
  name: string;
  sub?: string;
  /**
   * Optional control displayed immediately after this stage's label. The
   * measurement tree receives `true` so an interactive control can avoid
   * fetching data or opening a popover while still contributing its width.
   */
  renderAccessory?: (measurement: boolean) => ReactNode;
}

export interface StageStepperProps {
  stages: readonly StageStepperStage[];
  activeStageId: string;
  doneStageIds?: readonly string[];
  lockedStageIds?: readonly string[];
  onStageClick?: (stageId: string) => void;
  /** Ad hoc inline overrides for this instance, layered on top of `styles.root` via the native `style` attribute. Unrelated to `styles`. */
  style?: CSSProperties;
  /** Swappable visual theme, e.g. behind a feature flag. Defaults to the current look. */
  styles?: StageStepperStyles;
}

export type ResolvedStageStepperStage = {
  readonly id: string;
  readonly index: number;
  readonly state: StageStepperItemState;
  readonly isActive: boolean;
  readonly isLast: boolean;
  readonly primaryLabel: string;
  readonly secondaryLabel?: string;
  readonly renderAccessory?: (measurement: boolean) => ReactNode;
};

/* ── StageStepper ── */

const FULL_MODE_WIDTH_PER_STAGE = 148;
const COMPACT_MODE_WIDTH_PER_STAGE = 74;
const SHORT_STEPPER_FULL_MODE_BONUS = 8;
const FULL_MODE_CONTENT_BUFFER = 8;

const FULL_MODE_MEASUREMENT_STYLE: CSSProperties = {
  position: 'fixed',
  left: -100000,
  top: -100000,
  display: 'flex',
  alignItems: 'center',
  width: 'max-content',
  minWidth: 'max-content',
  visibility: 'hidden',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
};

const getFullModeWidthPerStage = (stageCount: number) =>
  stageCount <= 3
    ? FULL_MODE_WIDTH_PER_STAGE + SHORT_STEPPER_FULL_MODE_BONUS
    : FULL_MODE_WIDTH_PER_STAGE;

export const getStageStepperDisplayMode = ({
  availableWidth,
  fullModeContentWidth,
  stageCount,
}: {
  availableWidth: number;
  fullModeContentWidth?: number;
  stageCount: number;
}): StageStepperDisplayMode => {
  if (stageCount <= 0) return 'minimal';

  const widthPerStage = availableWidth / stageCount;
  const fullModeWidthPerStage = getFullModeWidthPerStage(stageCount);

  if (widthPerStage < COMPACT_MODE_WIDTH_PER_STAGE) return 'minimal';
  if (
    fullModeContentWidth !== undefined
    && fullModeContentWidth > availableWidth - FULL_MODE_CONTENT_BUFFER
  ) {
    return 'compact';
  }
  if (widthPerStage >= fullModeWidthPerStage) return 'full';
  return 'compact';
};

export const getResolvedActiveStage = (
  stages: readonly StageStepperStage[],
  activeStageId: string,
): { activeStage: StageStepperStage; activeIndex: number } | null => {
  if (!stages.length) return null;

  const matchedIndex = stages.findIndex(stage => stage.id === activeStageId);
  const activeIndex = matchedIndex >= 0 ? matchedIndex : 0;

  return {
    activeStage: stages[activeIndex]!,
    activeIndex,
  };
};

const getStageStepperItemState = (
  stageId: string,
  activeStageId: string,
  doneStageIds: readonly string[],
  lockedStageIds: readonly string[],
): StageStepperItemState => {
  if (stageId === activeStageId) return 'active';
  if (doneStageIds.includes(stageId)) return 'done';
  if (lockedStageIds.includes(stageId)) return 'locked';
  return 'upcoming';
};

const getStageLabelParts = (stage: StageStepperStage) => ({
  primaryLabel: stage.name || stage.sub || stage.id,
  secondaryLabel: stage.name && stage.sub ? stage.sub : undefined,
});

type StageStepperMeasurements = {
  availableWidth: number | null;
  fullModeContentWidth: number | null;
};

const useObservedWidth = () => {
  const [measurements, setMeasurements] = useState<StageStepperMeasurements>({
    availableWidth: null,
    fullModeContentWidth: null,
  });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const measurementRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const node = rootRef.current;
    const measurementNode = measurementRef.current;
    if (!node || !measurementNode || typeof ResizeObserver === 'undefined') return;

    const applyMeasurements = () => {
      const nextAvailableWidth = Math.round(node.getBoundingClientRect().width);
      const nextFullModeContentWidth = Math.round(measurementNode.getBoundingClientRect().width);
      if (nextAvailableWidth <= 0 || nextFullModeContentWidth <= 0) return;

      setMeasurements(currentMeasurements => {
        if (
          currentMeasurements.availableWidth === nextAvailableWidth
          && currentMeasurements.fullModeContentWidth === nextFullModeContentWidth
        ) {
          return currentMeasurements;
        }

        return {
          availableWidth: nextAvailableWidth,
          fullModeContentWidth: nextFullModeContentWidth,
        };
      });
    };

    applyMeasurements();

    const observer = new ResizeObserver(applyMeasurements);

    observer.observe(node);
    observer.observe(measurementNode);

    return () => observer.disconnect();
  }, []);

  return { measurements, measurementRef, rootRef };
};

const getResolvedStages = ({
  stages,
  activeStageId,
  doneStageIds,
  lockedStageIds,
}: {
  stages: readonly StageStepperStage[];
  activeStageId: string;
  doneStageIds: readonly string[];
  lockedStageIds: readonly string[];
}): ResolvedStageStepperStage[] =>
  stages.map((stage, index) => {
    const { primaryLabel, secondaryLabel } = getStageLabelParts(stage);
    const state = getStageStepperItemState(stage.id, activeStageId, doneStageIds, lockedStageIds);

    return {
      id: stage.id,
      index,
      state,
      isActive: stage.id === activeStageId,
      isLast: index === stages.length - 1,
      primaryLabel,
      secondaryLabel,
      renderAccessory: stage.renderAccessory,
    };
  });

export const StageStepper = ({
  stages,
  activeStageId,
  doneStageIds = [],
  lockedStageIds = [],
  onStageClick,
  style,
  styles = DEFAULT_STAGE_STEPPER_STYLES,
}: StageStepperProps) => {
  const { measurements, measurementRef, rootRef } = useObservedWidth();

  const resolvedActiveStage = getResolvedActiveStage(stages, activeStageId);
  if (!resolvedActiveStage) return null;

  const { activeStage, activeIndex } = resolvedActiveStage;
  const resolvedStages = getResolvedStages({
    stages,
    activeStageId: activeStage.id,
    doneStageIds,
    lockedStageIds,
  });
  const mode = getStageStepperDisplayMode({
    availableWidth: measurements.availableWidth ?? Number.POSITIVE_INFINITY,
    fullModeContentWidth: measurements.fullModeContentWidth ?? undefined,
    stageCount: stages.length,
  });
  const activeStageLabels = getStageLabelParts(activeStage);

  return (
    <>
      <div
        ref={rootRef}
        data-stage-stepper='true'
        data-stage-stepper-mode={mode}
        css={[styles.root, mode === 'full' ? styles.rootGapFull : styles.rootGapCompact]}
        style={style}
      >
        {mode === 'minimal'
          ? (
            <StageStepperSummary
              activeIndex={activeIndex}
              stageCount={stages.length}
              primaryLabel={activeStageLabels.primaryLabel}
              styles={styles}
            />
          )
          : (
            resolvedStages.map(resolvedStage => (
              <StageStepperItem
                key={resolvedStage.id}
                resolvedStage={resolvedStage}
                mode={mode}
                onStageClick={onStageClick}
                styles={styles}
              />
            ))
          )}
      </div>
      <div
        ref={measurementRef}
        data-stage-stepper-measurement='true'
        aria-hidden='true'
        css={[styles.root, styles.rootGapFull]}
        style={FULL_MODE_MEASUREMENT_STYLE}
      >
        {resolvedStages.map(resolvedStage => (
          <StageStepperItem
            key={resolvedStage.id}
            resolvedStage={resolvedStage}
            mode='full'
            styles={styles}
            measurement
          />
        ))}
      </div>
    </>
  );
};
